import { Component, OnInit } from '@angular/core';
import { EChartsOption } from 'echarts';
import { forkJoin } from 'rxjs';
import { DashboardService } from '../dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FormatCurrencyPipe } from 'src/app/shared/pipes/formatCurrency.pipe';

@Component({
  selector: 'app-weekly-sales-graph',
  templateUrl: './weekly-sales-graph.component.html',
  styleUrls: ['./weekly-sales-graph.component.scss'],
  providers: [FormatCurrencyPipe]
})
export class WeeklySalesGraphComponent implements OnInit {
  public isLoaded: boolean = false;
  public echartHorizontalLineChart: EChartsOption = {};

  constructor(private dashBoardService: DashboardService, private translate: TranslateService,
    private formatCurrency: FormatCurrencyPipe) { }

  langSub!: Subscription;

  ngOnInit(): void {
    this.loadLastSales();

    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadLastSales();
    });
  }

  private readonly weekdayKey: Record<string, string> = {
    'SEG.': 'weekdays.mon',
    'TER.': 'weekdays.tue',
    'QUA.': 'weekdays.wed',
    'QUI.': 'weekdays.thu',
    'SEX.': 'weekdays.fri',
    'SÁB.': 'weekdays.sat',
    'DOM.': 'weekdays.sun'
  };

  private translateWeekLabels(labels: string[]): string[] {
    return labels.map(lbl => {
      const key = this.weekdayKey[lbl] ?? lbl;
      return this.translate.instant(key);
    });
  }

  loadLastSales(): void {
    this.isLoaded = true;

    forkJoin({
      Last7DaysSales: this.getLast7DaysSales(),
      last7DaysReceivedSales: this.getLast7DaysReceivedSales(),
    }).subscribe({
      next: (results) => {
        const Last7DaysSales = results.Last7DaysSales;
        const last7DaysReceivedSales = results.last7DaysReceivedSales;

        const translatedLabels = this.translateWeekLabels(Last7DaysSales.labels);

        this.translate.get([
          'common.generatedDeposits',
          'common.receivedDeposits'
        ]).subscribe((translations) => {
          this.mountGraph(
            translatedLabels,
            Last7DaysSales.values,
            last7DaysReceivedSales.values,
            translations['common.generatedDeposits'],
            translations['common.receivedDeposits']
          );
        });
      },
      error: (error) => {
        console.error('Erro ao carregar os saldos', error);
      },
      complete: () => {
        this.isLoaded = false;
      },
    });
  }

  getLast7DaysSales() {
    return this.dashBoardService.getLast7DaysSales().then((value) => {
      return value;
    });
  }

  getLast7DaysReceivedSales() {
    return this.dashBoardService.getLast7DaysReceivedSales().then((value) => {
      return value;
    });
  }

  mountGraph(
    labels: string[],
    last7DaysSales: number[],
    last7DaysReceivedSales: number[],
    labelGenerated: string,
    labelReceived: string
  ) {
    const fmt = (v: number) => this.formatCurrency.transform(v);

    const styles = getComputedStyle(document.documentElement);
    const chartPrimary = styles.getPropertyValue('--weekly-chart-primary').trim();
    const chartSecondary = styles.getPropertyValue('--weekly-chart-secondary').trim();

    this.echartHorizontalLineChart = {
      valueAxis: {
        axisLine: {
          lineStyle: {
            color: 'rgba(171, 167, 167,0.2)',
          },
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(171, 167, 167,0.2)'],
          },
        },
        splitLine: {
          lineStyle: {
            color: ['rgba(171, 167, 167,0.2)'],
          },
        },
      },
      grid: {
        top: '6',
        right: '0',
        bottom: '17',
        left: '70',
      },
      tooltip: {
        trigger: 'axis',
        position: ['35%', '32%'],
        formatter: ((params: any) => {
          const list = Array.isArray(params) ? params : [params];
          return list
            .map((p: any) => `${p.marker} ${p.seriesName}: <b>${fmt(+p.data)}</b>`)
            .join('<br>');
        }) as any
      },
      xAxis: {
        data: labels,
        axisLine: {
          lineStyle: {
            color: 'rgba(171, 167, 167,0.2)',
          },
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(171, 167, 167,0.2)',
          },
        },
        axisLabel: {
          fontSize: 10,
          color: '#5f6d7a',
        },
      },
      yAxis: {
        splitLine: {
          lineStyle: {
            color: 'rgba(171, 167, 167,0.2)',
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(171, 167, 167,0.2)',
          },
        },
        axisLabel: {
          fontSize: 10,
          color: '#5f6d7a',
          formatter: fmt
        },
      },
      series: [
        {
          name: labelGenerated,
          type: 'bar',
          barMaxWidth: 20,
          data: last7DaysSales,
        },
        {
          name: labelReceived,
          type: 'bar',
          barMaxWidth: 20,
          data: last7DaysReceivedSales,
        },
      ],
      color: [chartPrimary, chartSecondary],
    };
  }
}
